import React, { useEffect, useState } from "react";
import {
  Product,
  ProductProvider,
  useProductBase,
  useProductProviderContext,
} from "@made-people/grease-product";
import { useCentraContext } from "@made-people/grease-centra";
import { crc32 } from "@made-people/functions-crc32";
import { useBffClient } from "../../../hooks/use-bff-client";
import { useFrontendContext } from "../../../hooks/use-frontend-context";
import { frontendProductToGreaseProduct } from "../../../services/bff/mappers/frontend-product-to-grease-product";
import { useAllocatedWarehouseIds } from "../../../services/centra/allocation-rule/use-allocated-warehouse-ids";

export const AlgoliaCentraProductWrapper: React.FC<{
  children: React.ReactNode;
  isDrawerOpen: boolean;
}> = ({ children, isDrawerOpen }) => {
  const algoliaProductBase = useProductBase();
  const productId = algoliaProductBase.productId;

  const providerContext = useProductProviderContext();
  const { queryContext } = useCentraContext();
  const bffClient = useBffClient();
  const frontendContext = useFrontendContext();
  const allocatedWarehouseIds = useAllocatedWarehouseIds();

  const [centraProduct, setCentraProduct] = useState<Product | undefined>(
    undefined,
  );
  const queryHash = crc32(JSON.stringify({ productId, queryContext }));

  useEffect(() => {
    if (!isDrawerOpen) return;
    let cancelled = false;

    async function fetchProduct() {
      const response = await bffClient.fetchFrontendProductsCached(
        [productId],
        frontendContext,
      );
      if (cancelled) return;
      const frontendProduct = response?.frontendProducts?.[0];
      if (!frontendProduct) return;
      setCentraProduct(
        frontendProductToGreaseProduct(
          frontendProduct,
          frontendContext,
          allocatedWarehouseIds,
        ),
      );
    }
    fetchProduct();

    return () => {
      cancelled = true;
    };
  }, [queryHash, isDrawerOpen, allocatedWarehouseIds]);

  if (providerContext.type === "pdp") {
    console.warn("Using AlgoliaCentraProductWrapper in PDP context");
    return children;
  }

  if (!centraProduct) {
    return (
      <div className="p-4 min-w-[300px] lg:min-w-[448px] animate-pulse">
        <div className="flex gap-4 mb-6">
          <div className="w-20 h-24 bg-surface-2 rounded" />
          <div className="flex-1">
            <div className="h-4 bg-surface-2 rounded w-3/4 mb-2" />
            <div className="h-4 bg-surface-2 rounded w-1/4" />
          </div>
        </div>
        <div className="h-4 bg-surface-2 rounded w-1/3 mb-3" />
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-10 bg-surface-2 rounded" />
          ))}
        </div>
        <div className="h-12 bg-surface-2 rounded w-full" />
      </div>
    );
  }

  return (
    <ProductProvider product={centraProduct} context={{ type: "unknown" }}>
      {children}
    </ProductProvider>
  );
};
